import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  BadgeCheck,
  CalendarClock,
  CalendarPlus,
  ClipboardCheck,
  CheckCircle2,
  CircleDot,
  Clock3,
  Hourglass,
  Lock,
  Send,
  TreePalm,
  UserPlus,
  UserX,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge, Button, Spinner } from '@/components/ui';
import { getEmployeeTimeline } from '@/api/services/employment.service';
import { getErrorMessage } from '@/lib/getErrorMessage';

function formatInstant(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

const TYPE_META: Record<string, { label: string; icon: LucideIcon }> = {
  employment_created: { label: 'Employment', icon: UserPlus },
  lifecycle_change: { label: 'Lifecycle', icon: ArrowRightLeft },
  assignment_applied: { label: 'Assignment', icon: CheckCircle2 },
  assignment_scheduled: { label: 'Scheduled', icon: CalendarClock },
  leave: { label: 'Leave', icon: TreePalm },
  'movement.submitted': { label: 'Movement submitted', icon: Send },
  'movement.scheduled': { label: 'Movement scheduled', icon: Clock3 },
  'movement.applied': { label: 'Movement applied', icon: CheckCircle2 },
  'movement.rejected': { label: 'Movement rejected', icon: XCircle },
  'movement.cancelled': { label: 'Movement cancelled', icon: XCircle },
  'probation.opened': { label: 'Probation started', icon: Hourglass },
  'probation.review_submitted': { label: 'Probation review', icon: ClipboardCheck },
  'probation.extended': { label: 'Probation extended', icon: CalendarPlus },
  'probation.confirmed': { label: 'Probation confirmed', icon: BadgeCheck },
  'probation.ended': { label: 'Probation ended', icon: UserX },
  'case.opened': { label: 'HR case', icon: Lock },
  'case.decided': { label: 'HR case', icon: Lock },
  'case.warning_expired': { label: 'HR case', icon: Lock },
  'case.closed': { label: 'HR case', icon: Lock },
  'grievance.submitted': { label: 'Grievance', icon: Lock },
  'grievance.closed': { label: 'Grievance', icon: Lock },
};

function formatDay(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

/** The summary line; a warning's expiry arrives as a date so it is formatted here. */
function summaryOf(event: { summary: string | null; metadata?: Record<string, unknown> }) {
  const expires = formatDay(event.metadata?.expiresAt);
  if (!event.summary) return null;
  return expires ? `${event.summary}, expires ${expires}` : event.summary;
}

/** Known kinds get a curated label; anything newer degrades to a readable one. */
function eventMeta(type: unknown): { label: string; icon: LucideIcon } {
  if (typeof type === 'string' && Object.hasOwn(TYPE_META, type)) return TYPE_META[type];
  const text = typeof type === 'string' && type ? type.replace(/[._]+/g, ' ').trim() : 'event';
  return { label: text.charAt(0).toUpperCase() + text.slice(1), icon: CircleDot };
}

/** Cursor-paginated employee timeline merged from authoritative HR sources. */
export function EmployeeTimeline({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}): React.ReactElement {
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['hr-employee-timeline', companyId, userId, cursor ?? null],
    queryFn: () => getEmployeeTimeline(companyId, userId, { limit: 25, cursor }),
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-10" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Loading timeline</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-3" role="alert">
        <p className="text-sm text-danger">{getErrorMessage(error)}</p>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!data) return <></>;
  const events = Array.isArray(data.data) ? data.data : [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Timeline</h3>
        <span className="text-xs text-gray-600 dark:text-gray-300" aria-live="polite">
          {isFetching ? 'Updating…' : `${data.total ?? events.length} events`}
        </span>
      </div>
      {events.length === 0 ? (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
          No timeline events recorded yet.
        </p>
      ) : (
        <ol className="mt-3 divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {events.map((event, index) => {
            const meta = eventMeta(event.type);
            const Icon = meta.icon;
            return (
              <li key={event.id ?? index} className="space-y-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex min-w-0 items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                    <Icon
                      className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
                      aria-hidden
                    />
                    <span className="min-w-0 break-words">{event.title || meta.label}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    {event.source?.entity === 'hr_case' ? (
                      // Shown only to the case team and uninvolved owners and admins.
                      <Badge variant="danger">Confidential</Badge>
                    ) : (
                      event.visibility === 'hr_only' && <Badge variant="warning">HR only</Badge>
                    )}
                    <Badge variant="neutral">{meta.label}</Badge>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {formatInstant(event.occurredAt)}
                </p>
                {summaryOf(event) && (
                  <p
                    className={`text-sm text-gray-700 dark:text-gray-200 ${
                      event.source?.entity === 'hr_case' ? '' : 'capitalize'
                    }`}
                  >
                    {summaryOf(event)}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={cursors.length === 0}
          onClick={() => setCursors((current) => current.slice(0, -1))}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!data.nextCursor}
          onClick={() =>
            setCursors((current) => (data.nextCursor ? [...current, data.nextCursor] : current))
          }
        >
          Next
        </Button>
      </div>
    </div>
  );
}
